import { motion } from 'framer-motion';
import {
  AcademicCapIcon,
  UsersIcon,
  ChartBarIcon,
  DocumentTextIcon,
  HeartIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';

interface FeatureTile {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
}

const FEATURE_TILES: FeatureTile[] = [
  {
    icon: AcademicCapIcon,
    title: 'All 12 Steps',
    description: 'Guided reflection prompts for each step',
  },
  {
    icon: UsersIcon,
    title: '24 Fellowships',
    description: 'AA, NA, OA, CA, and 20 more supported',
  },
  {
    icon: ChartBarIcon,
    title: 'Step Progress',
    description: 'Track your progress with sponsor review',
  },
  {
    icon: DocumentTextIcon,
    title: 'Moral Inventory',
    description: 'Step 4 inventory tables, built in',
  },
  {
    icon: HeartIcon,
    title: 'Amends Tracking',
    description: 'Steps 8\u20139 amends list and completion',
  },
  {
    icon: PlusCircleIcon,
    title: 'Custom Fellowships',
    description: 'Add any program not on our list',
  },
];

const FELLOWSHIP_PILLS = ['AA', 'NA', 'OA', 'CA', 'MA', 'CMA', 'HA', 'EA', 'GA', 'SLA', 'SAA'];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

export function TwelveStepSection() {
  return (
    <section
      id="steps"
      className="bg-[#1B263B] py-20 px-4"
    >
      <div className="max-w-6xl mx-auto">
        {/* Heading */}
        <motion.div
          className="text-center mb-14"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Built on the original 12-step model.
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Daily Reprieve is grounded in the 12-step program as described in the Big Book of
            Alcoholics Anonymous &mdash; the original recovery model that has helped millions recover
            from addiction of every kind. We welcome anyone in recovery, from any fellowship.
          </p>
        </motion.div>

        {/* Feature grid */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-12"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {FEATURE_TILES.map((tile) => {
            const Icon = tile.icon;
            return (
              <motion.div
                key={tile.title}
                variants={fadeUp}
                className="rounded-2xl bg-white/5 border border-white/10 p-6 flex items-start gap-4"
              >
                <div className="p-2.5 rounded-xl bg-teal-500/15 shrink-0">
                  <Icon className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white mb-1">
                    {tile.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {tile.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Fellowship pills */}
        <motion.div
          className="flex flex-wrap items-center justify-center gap-2"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
        >
          {FELLOWSHIP_PILLS.map((pill) => (
            <motion.span
              key={pill}
              variants={fadeUp}
              className="bg-white/10 text-slate-300 rounded-full px-3 py-1 text-sm font-medium"
            >
              {pill}
            </motion.span>
          ))}
          <motion.span
            variants={fadeUp}
            className="bg-white/10 text-teal-400 rounded-full px-3 py-1 text-sm font-medium"
          >
            and 13 more
          </motion.span>
        </motion.div>
      </div>
    </section>
  );
}
